export type PayCycleState =
  | 'record_needed'
  | 'waiting_payday'
  | 'verify_needed'
  | 'evidence_insufficient'
  | 'input_incomplete'
  | 'verified';

export interface PayCycleInput {
  month: string;
  today: Date;
  shiftCount: number;
  evidenceGrade?: 'A' | 'B' | 'C';
  reconciliation?: 'needs_input' | 'match' | 'difference';
  paydayDay: number | 'last';
  online: boolean;
}

export interface PayCycleView {
  state: PayCycleState;
  activeStep: 'record' | 'verify' | 'done';
  title: string;
  body: string;
  action: { label: string; to: '/calendar' | '/report' | '/reconcile' };
  connectivityNote?: string;
}

function lastDay(year: number, monthIndex: number): number {
  return new Date(year, monthIndex + 1, 0).getDate();
}

export function paydayForMonth(month: string, paydayDay: number | 'last'): Date {
  const [year = 0, rawMonth = 0] = month.split('-').map(Number);
  const monthIndex = rawMonth - 1;
  const day = paydayDay === 'last' ? lastDay(year, monthIndex) : Math.min(paydayDay, lastDay(year, monthIndex));
  return new Date(year, monthIndex, day);
}

export function resolvePayCycle(input: PayCycleInput): PayCycleView {
  const connectivityNote = input.online ? undefined : '오프라인이어도 근무 기록과 명세 대조는 이 기기에서 계속할 수 있어요.';
  const connectivity = connectivityNote ? { connectivityNote } : {};
  if (input.shiftCount === 0) return {
    state: 'record_needed', activeStep: 'record', title: '이번 달 근무부터 기록해요',
    body: '달력에 근무를 넣으면 예상 수당과 계산 근거가 함께 만들어져요.', action: { label: '근무 기록하기', to: '/calendar' }, ...connectivity,
  };
  if (input.evidenceGrade === 'C') return {
    state: 'evidence_insufficient', activeStep: 'verify', title: '검증 전에 계산 근거를 보완해요',
    body: 'C등급에서는 확정 차액을 만들지 않아요. 누락된 통상임금·사업장·근로시간 정보를 먼저 확인해주세요.', action: { label: '부족한 근거 확인', to: '/report' }, ...connectivity,
  };
  if (input.reconciliation === 'match' || input.reconciliation === 'difference') return {
    state: 'verified', activeStep: 'done', title: input.reconciliation === 'match' ? '이번 달 대조를 마쳤어요' : '확인할 차이를 기록했어요',
    body: input.reconciliation === 'match' ? '명세서의 같은 지급 항목이 계산 참고 범위에 있어요.' : '항목별 차이와 근거를 저장했어요. 회사 계산 근거와 차례로 확인해보세요.',
    action: { label: '대조 결과 다시 보기', to: '/reconcile' }, ...connectivity,
  };
  if (input.reconciliation === 'needs_input') return {
    state: 'input_incomplete', activeStep: 'verify', title: '명세 입력을 마저 확인해요',
    body: '금액이나 같은 범위 확인이 남아 있어 아직 일치·차액을 확정하지 않았어요.', action: { label: '입력 보완하기', to: '/reconcile' }, ...connectivity,
  };
  const payday = paydayForMonth(input.month, input.paydayDay);
  const today = new Date(input.today.getFullYear(), input.today.getMonth(), input.today.getDate());
  if (today < payday) return {
    state: 'waiting_payday', activeStep: 'verify', title: `${payday.getMonth() + 1}월 ${payday.getDate()}일 명세를 기다리는 중`,
    body: '급여명세가 먼저 도착했다면 예정일 전에도 바로 대조할 수 있어요.', action: { label: '명세가 왔어요', to: '/reconcile' }, ...connectivity,
  };
  return {
    state: 'verify_needed', activeStep: 'verify', title: '급여명세와 같은 항목끼리 확인해요',
    body: '세전 지급 항목을 입력하면 빠진 범위 없이 일치·차이를 구분해요.', action: { label: '명세 대조하기', to: '/reconcile' }, ...connectivity,
  };
}
